import moment from "moment-timezone";
import { X as XIcon, Archive as ArchiveIcon } from "lucide-react";
import { CommonDestinationTimezone } from "@/lib/api/utils";
import { RiskMerchValue } from "@/lib/api/risk/merchValues";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// Month → color mapping
// ---------------------------------------------------------------------------

const MONTH_COLORS: Record<number, { bg: string; border: string; text: string }> = {
  0:  { bg: "bg-blue-50",    border: "border-blue-300",    text: "text-blue-800" },
  1:  { bg: "bg-purple-50",  border: "border-purple-300",  text: "text-purple-800" },
  2:  { bg: "bg-green-50",   border: "border-green-300",   text: "text-green-800" },
  3:  { bg: "bg-yellow-50",  border: "border-yellow-300",  text: "text-yellow-800" },
  4:  { bg: "bg-pink-50",    border: "border-pink-300",    text: "text-pink-800" },
  5:  { bg: "bg-cyan-50",    border: "border-cyan-300",    text: "text-cyan-800" },
  6:  { bg: "bg-orange-50",  border: "border-orange-300",  text: "text-orange-800" },
  7:  { bg: "bg-teal-50",    border: "border-teal-300",    text: "text-teal-800" },
  8:  { bg: "bg-indigo-50",  border: "border-indigo-300",  text: "text-indigo-800" },
  9:  { bg: "bg-red-50",     border: "border-red-300",     text: "text-red-800" },
  10: { bg: "bg-emerald-50", border: "border-emerald-300", text: "text-emerald-800" },
  11: { bg: "bg-violet-50",  border: "border-violet-300",  text: "text-violet-800" },
};

function getMonthColor(monthIndex: number) {
  return MONTH_COLORS[monthIndex] ?? MONTH_COLORS[0];
}

// ---------------------------------------------------------------------------
// Date formatting — "Mar 3rd" style
// ---------------------------------------------------------------------------

function formatRangeDate(isoString: string): string {
  return moment.tz(isoString, CommonDestinationTimezone).format("MMM Do");
}

// ---------------------------------------------------------------------------
// Card component
// ---------------------------------------------------------------------------

interface MerchValueCardProps {
  item: RiskMerchValue;
  onClick: (item: RiskMerchValue) => void;
  onDelete: (item: RiskMerchValue) => void;
  onArchive: (item: RiskMerchValue) => void;
}

export function MerchValueCard({ item, onClick, onDelete, onArchive }: MerchValueCardProps) {
  const startMoment = moment.tz(item.range_start, CommonDestinationTimezone);
  const monthIndex = startMoment.month();
  const color = getMonthColor(monthIndex);

  const dateLabel = `${formatRangeDate(item.range_start)} → ${formatRangeDate(item.range_end)}`;

  return (
    <div
      data-testid="merch-value-card"
      className={cn(
        "relative group rounded border px-2.5 py-1.5 cursor-pointer transition-shadow hover:shadow-md",
        "flex flex-col gap-0.5 min-w-[180px] max-w-[250px]",
        color.bg,
        color.border,
      )}
      onClick={() => onClick(item)}
    >
      {/* Date range */}
      <span className={cn("text-[11px] font-medium leading-tight flex flex-row justify-between", color.text)}>
        {dateLabel}
      {/* Archive + Delete buttons */}
      <div className="flex gap-2">
        <button
          data-testid="merch-value-card-archive"
          className="p-0.5 rounded hover:bg-black/10"
          onClick={(e) => {
            e.stopPropagation();
            onArchive(item);
          }}
        >
          <ArchiveIcon className="h-3 w-3 text-muted-foreground" />
        </button>
        <button
          data-testid="merch-value-card-delete"
          className="p-0.5 rounded hover:bg-black/10"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(item);
          }}
        >
          <XIcon className="h-3 w-3 text-muted-foreground" />
        </button>
      </div>


      </span>

      {/* Value */}
        <span className="text-sm font-bold leading-tight truncate">
          {item.value ?? ""}
        </span>

      {/* Note */}
        <span className="text-[11px] text-muted-foreground leading-tight truncate">
          {item.note ?? ""}
        </span>
    </div>
  );
}
